/**
 * InfrastructurePage
 *
 * Main page for infrastructure configuration management.
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T017 - Create InfrastructurePage with ACR section
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Requirements:
 * - US1: ACR Configuration
 * - US2: ACI Configuration (Phase 4)
 * - US3: Managed Identity (Phase 5)
 * - US5: Key Vault References (Phase 7)
 *
 * Conformed to Spec 197 design blueprint — presentation migrated onto the
 * canonical Tailwind+token UI kit (PageLayout/Card/StatCard/Badge/Button/
 * EmptyState/LoadingState + token underline tabs). No data/logic/behavior
 * change; the ACR/ACI/KeyVault forms + Discovery modal are unchanged sub-components.
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Box, Search, X, CheckCircle, Info } from 'lucide-react';
import { useInfrastructureConfig, useInfrastructureHealth } from '../hooks/useInfrastructure';
import { AcrConfigForm } from '../components/AcrConfigForm';
import { AciDefaultsForm } from '../components/AciDefaultsForm';
import { AciOverridesTable } from '../components/AciOverridesTable';
import { KeyVaultReferencesForm } from '../components/KeyVaultReferencesForm';
import { DiscoverFromAzureModal } from '../components/DiscoverFromAzureModal';
import {
  CollectedSignal,
  ConfigValidationResult,
  DiscoverySelection,
  AcrConfigDto,
  AciConfigDto,
  KeyVaultConfigDto,
} from '../types';
import {
  PageLayout,
  Card,
  Button,
  Badge,
  StatCard,
  EmptyState,
  LoadingState,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import type { BadgeProps } from '../../../components/ui';
import { infrastructureHelp } from '../../../data/help/infrastructureHelp';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';

type TabId = 'acr' | 'aci' | 'keyvault' | 'health';

interface TabConfig {
  id: TabId;
  label: string;
  description: string;
  icon: string;
}

const tabs: TabConfig[] = [
  { id: 'acr', label: 'Container Registry', description: 'ACR settings', icon: '📦' },
  { id: 'aci', label: 'Container Instances', description: 'ACI defaults', icon: '🖥️' },
  { id: 'keyvault', label: 'Key Vault', description: 'Secret references', icon: '🔐' },
  { id: 'health', label: 'Health Status', description: 'Component health', icon: '💚' },
];

// Spec 216 T024: health is the collected DB-only read model ({ok, attention,
// stale, not-collected} signals), not the old live-probe statuses.
const getHealthBadgeVariant = (signal?: CollectedSignal): BadgeProps['variant'] => {
  switch (signal) {
    case 'ok':
      return 'success';
    case 'stale':
      return 'warning';
    case 'attention':
      return 'error';
    default:
      return 'secondary';
  }
};

const getHealthStatusLabel = (signal?: CollectedSignal): string => {
  switch (signal) {
    case 'ok':
      return 'Healthy';
    case 'stale':
      return 'Stale';
    case 'attention':
      return 'Attention';
    case 'not-collected':
      return 'Not collected';
    default:
      return 'Unknown';
  }
};

export const InfrastructurePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('acr');
  const [, setValidationResult] = useState<ConfigValidationResult | null>(null);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [discoveredData, setDiscoveredData] = useState<{
    acr?: AcrConfigDto;
    aci?: Partial<AciConfigDto>;
    keyVault?: KeyVaultConfigDto;
    key: number;
  } | null>(null);

  const { data: config, isLoading, error, refetch } = useInfrastructureConfig();
  const { data: health } = useInfrastructureHealth(true, 60000); // Poll every minute

  const handleConfigSuccess = () => {
    refetch();
    setValidationResult(null);
  };

  const handleDiscoveryApply = (selection: DiscoverySelection) => {
    const patch: typeof discoveredData = { key: Date.now() };

    if (selection.registry) {
      patch.acr = {
        registryName: selection.registry.name,
        resourceGroup: selection.registry.resourceGroup,
        subscriptionId: selection.aciSubscriptionId,
        loginServer: selection.registry.loginServer,
      };
    } else if (selection.aciSubscriptionId) {
      // No ACR found — pre-fill subscription (and resource group if available) so user
      // doesn't have to retype them when filling in the registry name manually.
      patch.acr = {
        registryName: null,
        resourceGroup: selection.aciResourceGroup || null,
        subscriptionId: selection.aciSubscriptionId,
      };
    }

    if (selection.aciResourceGroup) {
      patch.aci = {
        resourceGroup: selection.aciResourceGroup,
        subscriptionId: selection.aciSubscriptionId,
      };
    }
    if (selection.keyVault) {
      patch.keyVault = {
        url: selection.keyVault.vaultUri,
        useManagedIdentity: true,
      };
    }
    setDiscoveredData(patch);
  };

  // Default ACI config for new setups
  const defaultAciConfig = {
    resourceGroup: '',
    subscriptionId: '',
    defaults: {
      cpuCores: 1.0,
      memoryGb: 2.0,
      timeoutMinutes: 60,
      restartPolicy: 'Never' as const,
      location: 'eastus2',
    },
  };

  // Default Key Vault config for new setups
  const defaultKeyVaultConfig = {
    url: '',
    useManagedIdentity: true,
  };

  // Effective ACI config: saved config wins, then overlay discovered resourceGroup/subscriptionId,
  // then fall back to defaults. All three save buttons (ACR, ACI, KV) include this in their payload,
  // so it must always be a complete AciConfigDto with non-empty required fields.
  const effectiveAciConfig = config?.aci ?? {
    ...defaultAciConfig,
    resourceGroup: discoveredData?.aci?.resourceGroup ?? '',
    subscriptionId: discoveredData?.aci?.subscriptionId ?? '',
  };

  // Effective Key Vault config: discovered URL wins over empty default.
  const effectiveKeyVaultConfig = discoveredData?.keyVault ?? config?.keyVault ?? defaultKeyVaultConfig;

  // First-time setup: API returns null (not an error) when no config exists in DB
  const isFirstTimeSetup = !isLoading && config === null && !error;

  // Spec 216 T024: health.components is the collected component list.
  const healthComponents = health?.components ?? [];

  if (isLoading) {
    return (
      <PageLayout
        title="Infrastructure Configuration"
        description="Manage Azure resources for the backup infrastructure"
        icon={<Box className="h-6 w-6" />}
      >
        <Card padding="none" className="p-6">
          <LoadingState rows={6} />
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Infrastructure Configuration"
      description="Manage Azure resources for the backup infrastructure"
      icon={<Box className="h-6 w-6" />}
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Search className="h-4 w-4" />}
            onClick={() => setShowDiscoverModal(true)}
          >
            Discover from Azure
          </Button>
          <PageHelpPanel content={infrastructureHelp} buttonSize="sm" />
          <AppTooltip title="Overall infrastructure health status across all components">
            <Badge variant={getHealthBadgeVariant(health?.overall)} size="md">
              {getHealthStatusLabel(health?.overall)}
            </Badge>
          </AppTooltip>
          <span className="text-xs text-[var(--color-text-secondary)]">
            {config?.environment || 'DEV'} Environment
          </span>
        </>
      }
    >
      {/* Discovery Modal */}
      <DiscoverFromAzureModal
        isOpen={showDiscoverModal}
        onClose={() => setShowDiscoverModal(false)}
        onApply={handleDiscoveryApply}
      />

      {/* Discovered Data Banner */}
      {discoveredData && (
        <Card padding="none" className="mb-6 flex items-start justify-between border-[var(--color-status-success)]/40 bg-[var(--color-status-success)]/5 p-4">
          <div className="flex">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--color-status-success)]" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
                Forms pre-filled from Azure discovery
              </h3>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                Review the values in each tab and click "Save Configuration" to persist them.
              </p>
            </div>
          </div>
          <button
            onClick={() => setDiscoveredData(null)}
            className="ml-4 flex-shrink-0 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </Card>
      )}

      {/* First-Time Setup Banner */}
      {isFirstTimeSetup && (
        <Card padding="none" className="mb-6 border-[var(--color-accent)]/40 bg-[var(--color-accent)]/5 p-4">
          <div className="flex">
            <Info className="h-5 w-5 flex-shrink-0 text-[var(--color-accent)]" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
                First-Time Setup Required
              </h3>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                No infrastructure configuration exists for this environment. Please configure at
                least the ACI settings and Key Vault URL to enable backup operations.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="mb-6 flex gap-1 border-b border-[var(--color-border)]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              '-mb-px inline-flex items-center border-b-2 px-4 py-3 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'acr' && (
          <AppTooltip title="Configure the Azure Container Registry used for backup container images">
            <div>
              <AcrConfigForm
                key={discoveredData?.key ?? 'saved'}
                initialData={discoveredData?.acr ?? config?.acr}
                aciConfig={effectiveAciConfig}
                keyVaultConfig={effectiveKeyVaultConfig}
                onSuccess={handleConfigSuccess}
                onValidationResult={setValidationResult}
              />
            </div>
          </AppTooltip>
        )}

        {activeTab === 'aci' && (
          <div className="space-y-6">
            <AciDefaultsForm
              key={discoveredData?.key ?? 'saved'}
              initialData={effectiveAciConfig}
              acrConfig={discoveredData?.acr ?? config?.acr}
              keyVaultConfig={effectiveKeyVaultConfig}
              onSuccess={handleConfigSuccess}
            />
            {config ? (
              <AciOverridesTable onUpdate={handleConfigSuccess} />
            ) : (
              <Card padding="none" className="p-6">
                <EmptyState message="Save ACI defaults first to manage per-capacity-type overrides." />
              </Card>
            )}
          </div>
        )}

        {activeTab === 'keyvault' &&
          (config ? (
            <div className="space-y-4">
              {/* Auth mode summary — surfaces use_managed_identity (already in DTO) */}
              <Card padding="none" className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-medium text-[var(--color-text-primary)]">
                  Authentication Mode
                </span>
                <Badge variant={config.keyVault?.useManagedIdentity ? 'success' : 'secondary'} isOutlined>
                  {config.keyVault?.useManagedIdentity ? 'Managed Identity' : 'Service Principal / Secret'}
                </Badge>
              </Card>
              <KeyVaultReferencesForm
                key={discoveredData?.key ?? 'saved'}
                keyVaultUrl={effectiveKeyVaultConfig.url || config?.keyVault?.url}
                onUpdate={handleConfigSuccess}
              />
            </div>
          ) : (
            <Card padding="none" className="p-8">
              <EmptyState message="Save the ACI and Key Vault configuration first to manage secret references." />
            </Card>
          ))}

        {activeTab === 'health' && (
          <Card padding="none">
            <div className="border-b border-[var(--color-border)] px-6 py-4">
              <h3 className="text-lg font-medium text-[var(--color-text-primary)]">Health Status</h3>
              <p className="text-sm text-[var(--color-text-secondary)]">
                Current status of infrastructure components
              </p>
            </div>
            <div className="p-6">
              {health && healthComponents.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {healthComponents.map((component) => (
                    <StatCard
                      key={component.component}
                      label={component.component}
                      value={
                        <Badge variant={getHealthBadgeVariant(component.signal)} isOutlined>
                          {getHealthStatusLabel(component.signal)}
                        </Badge>
                      }
                      subtext={component.message || component.criterion}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  message="No health data available yet."
                  action={
                    <p className="text-xs text-[var(--color-text-secondary)]">
                      Health checks will be implemented in Phase 6 (T036-T043)
                    </p>
                  }
                />
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Footer Info */}
      <div className="mt-8 border-t border-[var(--color-border)] pt-6">
        <p className="text-center text-xs text-[var(--color-text-secondary)]">
          Constitution v1.8.0 • Type 4 (Platform) Infrastructure •{' '}
          {config?.id ? `Config ID: ${config.id.substring(0, 8)}...` : 'No configuration'}
        </p>
        {config && (config.updatedAt || config.createdAt || config.createdBy) && (
          <p className="mt-1 text-center text-xs text-[var(--color-text-secondary)]">
            {config.updatedAt && <>Last updated {formatEastern(config.updatedAt)}</>}
            {config.createdAt && <> • Created {formatEasternDate(config.createdAt)}</>}
            {config.createdBy && <> by {config.createdBy}</>}
          </p>
        )}
      </div>
    </PageLayout>
  );
};

export default InfrastructurePage;
